import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { getTicketDetails } from "../api/ticketsApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faCheckCircle } from "@fortawesome/free-solid-svg-icons";

const ProgressStatus = () => {
  const { id } = useParams();
  const [isLoading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [progressStatusList, setProgressStatusList] = useState([]);
  const [error, setError] = useState("");
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    const fetchTicketDetails = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await getTicketDetails(id);
        if (!isMounted.current) return;

        if (response.status === 200) {
          setDetails(response.data.ticket);
          setProgressStatusList(response.data.progressStatuses || []);
        } else {
          setError(response.data?.message || "Failed to fetch ticket details.");
        }
      } catch (err) {
        if (!isMounted.current) return;
        setError(
          err.response?.data?.message ||
          "An error occurred while fetching details. Please try again later."
        );
      } finally {
        if (isMounted.current) setLoading(false);
      }
    };

    fetchTicketDetails();
    return () => {
      isMounted.current = false;
    };
  }, [id]);

  const formatKey = (key) => {
    const spaced = key.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
    const spacedWithNumbers = spaced.replace(/([a-zA-Z])(\d)/g, "$1 $2");
    return spacedWithNumbers
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="bg-red-50 text-red-700 px-6 py-4 rounded-lg shadow-sm text-center max-w-md">
          <p className="font-medium mb-2 text-lg">Error</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="flex justify-center items-center h-screen text-gray-500">
        <p>No details available.</p>
      </div>
    );
  }

  const sortedStatuses = [...progressStatusList].sort((a, b) => a.rank - b.rank);
  const currentStatus = sortedStatuses.find(
    (status) => status.name === details.progressStatusName
  );
  const currentRank = currentStatus ? currentStatus.rank : 0;
  const totalSteps = sortedStatuses.length;
  const isFullyComplete = currentRank === totalSteps;

  // Determine progress line color
  const progressLineColor = "linear-gradient(to right, #ff9900, #ff6600)"; // Orange

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8">
      <div className="bg-white rounded-lg shadow-md w-full max-w-3xl p-6">
        {/* Organization Header */}
        <div className="text-center text-2xl font-bold text-white bg-orange-500 py-3 rounded-md shadow mb-6">
          {details.organizationName}
        </div>

        {/* Ticket Info - Card UI */}
        <div className="mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="bg-gray-50 px-5 py-3 border-b border-gray-200">
              <h3 className="text-center text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Customer Complaint Entry
              </h3>
            </div>

            {/* Body - Grid Layout */}
            <div className="p-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                {/* Ticket No */}
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket No</dt>
                  <dd className="mt-1 text-sm font-medium text-gray-900">{details.id}</dd>
                </div>

                {/* Ticket Head */}
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket Head</dt>
                  <dd className="mt-1 text-sm font-medium text-gray-900">{details.problemHeadName}</dd>
                </div>

                {/* Category */}
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Category</dt>
                  <dd className="mt-1 text-sm font-medium text-gray-900">{details.problemCategoryName}</dd>
                </div>

                {/* Entry Time */}
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Entry Time</dt>
                  <dd className="mt-1 text-sm font-medium text-gray-900">{details.createdAt}</dd>
                </div>

                {/* Created By */}
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</dt>
                  <dd className="mt-1 text-sm font-medium text-gray-900">
                    {details.assignee || details.createdBy}
                  </dd>
                </div>

                {/* Status */}
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Status</dt>
                  <dd className="mt-1 text-sm font-medium text-gray-900">{details.progressStatusName}</dd>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Stepper */}
        {sortedStatuses.length > 0 && (
          <div className="relative w-full max-w-3xl mx-auto mt-10 px-4 h-[4rem]">
            {/* Stepper Bar: Only circles and lines */}
            <div className="relative flex items-center justify-between w-full max-w-3xl mx-auto h-10">
              {/* Gray background line */}
              <div className="absolute top-1/2 left-0 w-full h-5 bg-gray-200 border-[3px] border-gray-300 rounded-full -translate-y-1/2 z-0"></div>

              {/* Colored progress line */}
              <div
                className="absolute top-1/2 left-0 h-5 rounded-full border-[3px] border-gray-300 -translate-y-1/2 transition-all duration-700 z-1"
                style={{
                  width:
                    totalSteps === 1
                      ? isFullyComplete ? '100%' : '0%'
                      : `${((currentRank - 1) / (totalSteps - 1)) * 100}%`,
                  background: progressLineColor,
                }}
              ></div>

              {/* Circles only (no labels here) */}
              {sortedStatuses.map((status) => {
                const isCompleted = status.rank <= currentRank;
                const isActive = status.rank === currentRank;
                const isFinalAndComplete = isActive && isFullyComplete;
                const showCheckmark = isCompleted || isFinalAndComplete;

                return (
                  <div key={status.id} className="relative z-10 flex items-center justify-center">
                    <div
                      className={`w-10 h-10 rounded-full border-[6px] flex items-center justify-center shadow-md transition-all duration-300 ${showCheckmark
                        ? "bg-green-400 border-white-400 text-white shadow-white-400"
                        : "bg-white border-white-300 shadow-gray-200"
                        }`}
                    >
                      {showCheckmark ? (
                        <FontAwesomeIcon icon={faCheck} className="w-5 h-5" />
                      ) : (
                        <div className="w-3 h-3 rounded-full bg-white-300"></div>
                      )}
                    </div>
                    {/* Label below circle */}
                    <div className="mt-[5rem] w-max absolute left-1/2 mt-4 transform -translate-x-1/2 text-center">
                      <span
                        className="text-sm font-medium text-black transition-all duration-300"
                      >
                        {status.name}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressStatus;